import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Video } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";
import { fmtDayLabel, fmtTime, istInputToIso, istDateKey } from "@/lib/time";
import { caseLabel } from "@/lib/labels";
import FilterPill from "@/components/FilterPill";
import useRowKeyboardNav from "@/lib/useRowKeyboardNav";

function _istToday() { return istDateKey(new Date()); }
function _istTomorrow() { const d = new Date(); d.setDate(d.getDate() + 1); return istDateKey(d); }
function _ist7DaysOut() { const d = new Date(); d.setDate(d.getDate() + 7); return istDateKey(d); }

export default function Hearings() {
    const [items, setItems] = useState([]);
    const [cases, setCases] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);    // hearing being edited (one-click)
    const [editSaving, setEditSaving] = useState(false);
    const [form, setForm] = useState({ case_id: "", scheduled_at: "", court: "", purpose: "", vc_url: "" });
    const [saving, setSaving] = useState(false);
    const [suggestion, setSuggestion] = useState(null);    // {url, scope}
    const [params] = useSearchParams();
    const range = params.get("range");    // today | tomorrow | upcoming | null
    const openParam = params.get("open");    // hearing id for deep-link auto-open

    const load = async () => {
        try {
            const [h, c] = await Promise.all([api.get("/hearings"), api.get("/cases")]);
            setItems(h.data); setCases(c.data);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    // Auto-suggest VC URL whenever the picked case (or court) changes.
    useEffect(() => {
        if (!form.case_id) { setSuggestion(null); return; }
        const c = cases.find(x => x.id === form.case_id);
        const court = form.court?.trim() || c?.court?.trim();
        if (!court) { setSuggestion(null); return; }
        const params = { court };
        if (c?.category) params.category = c.category;
        if (c?.bench) params.bench = c.bench;
        if (c?.hearing_type) params.hearing_type = c.hearing_type;
        api.get("/court-vc-links/match", { params })
            .then(r => setSuggestion(r.data?.matched ? r.data : null))
            .catch(() => setSuggestion(null));
        // pre-fill court from the litigation if user hasn't typed one
        if (!form.court && c?.court) setForm(f => ({ ...f, court: c.court }));
    }, [form.case_id, form.court, cases]);

    const useSuggestion = () => {
        if (suggestion?.url) setForm(f => ({ ...f, vc_url: suggestion.url }));
    };

    const submit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await api.post("/hearings", {
                ...form,
                scheduled_at: istInputToIso(form.scheduled_at),
            });
            toast.success("Hearing scheduled");
            setOpen(false);
            setForm({ case_id: "", scheduled_at: "", court: "", purpose: "", vc_url: "" });
            setSuggestion(null);
            load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const update = async (id, patch) => {
        const reason = window.prompt("Reason for this update (mandatory · logged to audit):");
        if (!reason || reason.trim().length < 3) {
            toast.error("Update requires a reason of at least 3 characters");
            return;
        }
        try {
            await api.patch(`/hearings/${id}`, patch, { headers: { "X-Change-Reason": reason.trim() } });
            load();
            toast.success("Hearing updated · logged to audit trail");
        } catch (e) { toast.error(apiError(e)); }
    };

    // group by date
    const visibleItems = useMemo(() => {
        // Hide cancelled hearings from every range filter — they're never
        // actionable. "All" range still shows them.
        const live = items.filter(h => String(h.status || "").toLowerCase() !== "cancelled");
        if (!range) return items;
        const today = _istToday();
        if (range === "today")    return live.filter(h => istDateKey(h.scheduled_at) === today);
        if (range === "tomorrow") return live.filter(h => istDateKey(h.scheduled_at) === _istTomorrow());
        if (range === "upcoming") {
            const max = _ist7DaysOut();
            return live.filter(h => {
                const k = istDateKey(h.scheduled_at);
                return k >= today && k <= max;
            });
        }
        return items;
    }, [items, range, params]);

    const grouped = visibleItems.reduce((acc, h) => {
        const day = fmtDayLabel(h.scheduled_at);
        (acc[day] = acc[day] || []).push(h); return acc;
    }, {});

    const rangeLabel = { today: "Today only", tomorrow: "Tomorrow only", upcoming: "Upcoming · next 7 days" }[range];

    const openHearingEdit = (h) => setEdit({
        id: h.id,
        scheduled_at: h.scheduled_at ? h.scheduled_at.slice(0, 16) : "",
        court: h.court || "",
        purpose: h.purpose || "",
        vc_url: h.vc_url || "",
        status: h.status || "scheduled",
        reason: "",
    });
    const { getRowProps } = useRowKeyboardNav({ items: visibleItems, onOpen: openHearingEdit, disabled: !!edit || open });
    let _flatIdx = -1;

    useEffect(() => {
        if (!openParam || edit) return;
        const h = items.find(x => x.id === openParam);
        if (h) openHearingEdit(h);
    }, [openParam, items]);    // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <PageHeader
                title="Court diary"
                subtitle="Hearings, outcomes and next dates — your firm's running calendar."
                action={<GoldButton onClick={() => setOpen(true)} data-testid="hearings-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Schedule</GoldButton>}
            />

            {rangeLabel && (
                <div className="mb-4 flex items-center gap-2" data-testid="hearings-filter-banner">
                    <FilterPill label={rangeLabel} testId={`hearings-pill-${range}`}/>
                    <span className="text-xs text-muted-foreground">{visibleItems.length} hearing{visibleItems.length === 1 ? "" : "s"}</span>
                </div>
            )}

            {visibleItems.length === 0 ? (
                <EmptyState title={rangeLabel ? `No hearings ${range === "today" ? "today" : range === "tomorrow" ? "tomorrow" : "in the next 7 days"}` : "Calendar is clear"} subtitle={rangeLabel ? "Try clearing the filter to see all hearings." : "No hearings scheduled. Add one to start the diary."} action={<GoldButton onClick={() => setOpen(true)} data-testid="hearings-empty-add">Schedule hearing</GoldButton>}/>
            ) : (
                <div className="space-y-10">
                    {Object.entries(grouped).map(([day, hs]) => (
                        <div key={day}>
                            <div className="overline mb-4">{day}</div>
                            <div className="border border-border divide-y divide-border">
                                {hs.map(h => { _flatIdx += 1; return (
                                    <div key={h.id} {...getRowProps(_flatIdx)}
                                        className="p-4 sm:px-5 sm:py-4 hover:bg-muted/50 transition-colors flex flex-col sm:flex-row sm:items-center sm:gap-4"
                                        data-testid={`hearing-row-${h.id}`}>
                                        {/* Row body — stacks on mobile, side-by-side on sm+ */}
                                        <button type="button" onClick={() => openHearingEdit(h)}
                                            data-testid={`hearing-open-${h.id}`}
                                            className="flex-1 min-w-0 text-left cursor-pointer flex items-start gap-3 sm:gap-6">
                                            {/* Time chip — bigger on mobile so it's easy to scan */}
                                            <div className="font-mono text-sm sm:text-sm text-primary w-16 sm:w-20 shrink-0 pt-0.5">
                                                {fmtTime(h.scheduled_at)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-foreground line-clamp-2 sm:truncate">
                                                    {h.case_title || "Case"}
                                                    <span className="text-muted-foreground font-mono text-[11px] ml-2 whitespace-nowrap">
                                                        {h.case_suit_number || h.case_number}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1 line-clamp-2 sm:line-clamp-1">
                                                    {h.purpose || "Hearing"} · {h.court || "—"}
                                                </div>
                                            </div>
                                        </button>
                                        {/* Actions row — full-width strip on mobile, inline on sm+ */}
                                        <div className="mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-border/60 flex items-center justify-between sm:justify-end gap-3 shrink-0">
                                            <StatusBadge status={h.status}/>
                                            {h.status !== "completed" && (
                                                <button onClick={() => update(h.id, { status: "completed" })}
                                                    data-testid={`hearing-complete-${h.id}`}
                                                    className="text-xs text-primary hover:underline whitespace-nowrap">
                                                    Mark complete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ); })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="Schedule hearing" testId="hearing-modal">
                <form onSubmit={submit} className="space-y-4">
                    <Field label="Litigation">
                        <Select required value={form.case_id} onChange={e => setForm({ ...form, case_id: e.target.value })} data-testid="hearing-case-select"
                            options={[{ value: "", label: "— select —" }, ...cases.map(c => ({ value: c.id, label: caseLabel(c) }))]} />
                    </Field>
                    <Field label="Date &amp; time (IST)">
                        <Input required type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} data-testid="hearing-datetime-input" />
                    </Field>
                    <Field label="Court"><Input value={form.court} onChange={e => setForm({ ...form, court: e.target.value })} placeholder="Court / bench" /></Field>
                    <Field label="VC URL (optional)" hint="Used by the 'Appear now' button on this hearing.">
                        <Input value={form.vc_url} onChange={e => setForm({ ...form, vc_url: e.target.value })} placeholder="https://vc.example.gov.in/..." data-testid="hearing-vc-url"/>
                    </Field>
                    {suggestion?.url && (
                        <div className="bg-background border border-primary/40 px-3 py-2 flex items-center gap-3" data-testid="hearing-vc-suggestion">
                            <Video className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5}/>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-primary">Detected VC link · {suggestion.scope === "tenant" ? "Firm" : "Global"}</div>
                                <div className="text-xs text-muted-foreground truncate font-mono">{suggestion.url}</div>
                            </div>
                            <button type="button" onClick={useSuggestion} data-testid="hearing-vc-use-suggestion"
                                className="text-xs text-primary hover:underline shrink-0">Use this</button>
                        </div>
                    )}
                    <Field label="Purpose"><Textarea rows="2" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. arguments / evidence" /></Field>
                    <div className="pt-2 flex justify-end"><GoldButton disabled={saving} data-testid="hearing-save-btn">{saving ? "Saving…" : "Schedule"}</GoldButton></div>
                </form>
            </Modal>

            <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit hearing" testId="hearing-edit-modal">
                {edit && (
                    <form onSubmit={async e => {
                        e.preventDefault();
                        if (!edit.reason || edit.reason.trim().length < 3) {
                            toast.error("Reason required (min 3 chars) — every edit is logged."); return;
                        }
                        setEditSaving(true);
                        try {
                            const { id, reason, scheduled_at, ...rest } = edit;
                            const patch = { ...rest };
                            if (scheduled_at) patch.scheduled_at = istInputToIso(scheduled_at);
                            await api.patch(`/hearings/${id}`, patch, { headers: { "X-Change-Reason": reason.trim() } });
                            toast.success("Hearing updated · logged to audit trail");
                            setEdit(null); load();
                        } catch (err) { toast.error(apiError(err)); }
                        finally { setEditSaving(false); }
                    }} className="space-y-4">
                        <Field label="Date &amp; time (IST)">
                            <Input required type="datetime-local" value={edit.scheduled_at}
                                   onChange={e => setEdit({ ...edit, scheduled_at: e.target.value })} data-testid="hearing-edit-datetime"/>
                        </Field>
                        <Field label="Court">
                            <Input value={edit.court} onChange={e => setEdit({ ...edit, court: e.target.value })} data-testid="hearing-edit-court"/>
                        </Field>
                        <Field label="Purpose">
                            <Textarea rows="2" value={edit.purpose} onChange={e => setEdit({ ...edit, purpose: e.target.value })} data-testid="hearing-edit-purpose"/>
                        </Field>
                        <Field label="VC URL (optional)">
                            <Input value={edit.vc_url} onChange={e => setEdit({ ...edit, vc_url: e.target.value })}
                                   placeholder="https://vc.example.gov.in/..." data-testid="hearing-edit-vc-url"/>
                        </Field>
                        <Field label="Status">
                            <Select value={edit.status} onChange={e => setEdit({ ...edit, status: e.target.value })}
                                    options={["scheduled", "completed", "adjourned", "cancelled"]} data-testid="hearing-edit-status"/>
                        </Field>
                        <Field label="Reason for change (min 3 chars · audit log)">
                            <Input required minLength={3} value={edit.reason}
                                   onChange={e => setEdit({ ...edit, reason: e.target.value })}
                                   placeholder="e.g. Court rescheduled" data-testid="hearing-edit-reason"/>
                        </Field>
                        <div className="flex justify-end pt-2"><GoldButton disabled={editSaving} data-testid="hearing-edit-save">{editSaving ? "Saving…" : "Save changes"}</GoldButton></div>
                    </form>
                )}
            </Modal>
        </>
    );
}